// Canonical /audiences library with real resource actions. Archived rows are
// retained in-memory as undo tombstones because the current list contract
// intentionally returns active audiences only.

import { ResponsiveFilters } from '@/components/responsive-filters';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Icons } from '@/components/primitives';
import { Dropdown } from '@/components/dropdowns';
import { MutationErrorLine } from '@/components/error-panel';
import { createDataTable } from '@/components/data-table';
import {
  useArchiveAudience,
  useAudiences,
  useCampaigns,
  useRefreshAudience,
  useRefreshAudienceSize,
  useRestoreAudience,
} from '@/api/hooks';
import { downloadCsv, csvFilename } from '@/lib/csv';
import { ago } from '@/lib/datetime';
import { isManagedAudience } from '@/lib/recent-buyers';
import { audienceSyncStateWords } from '@/lib/audience-sync-words';
import { visualTypeFromAudienceType } from '@/lib/audience-visual-rules';
import type { AudienceType, RuleGroup } from '@/api/types';
import { AudienceDetailDrawer, type ListedAudience } from './audience-drawer';
import { PageActions } from '@/components/page-actions';

function countLeaves(group: RuleGroup): number {
  return group.rules.reduce(
    (count, rule) => count + (rule.kind === 'group' ? countLeaves(rule) : 1),
    0,
  );
}

const ROW_H = 36;
const { Table } = createDataTable([
  { key: 'audience', header: 'Audience',            width: 280, mobile: 'title' },
  { key: 'type',     header: 'Type',                width: 110, mobile: 'subtitle' },
  { key: 'size',     header: 'Size',                width: 110, align: 'num', mobile: 'stat' },
  { key: 'used',     header: 'Used in productions', width: 130, align: 'num', priority: 2, mobile: 'stat' },
  { key: 'updated',  header: 'Last update',         width: 130, priority: 2, mobile: 'stat' },
  { key: 'sync',     header: 'Sync',                width: 110, mobile: 'status' },
  { key: 'measured', header: 'Size measured',       width: 110, priority: 3 },
  { key: 'rules',    header: 'Rules',               width: 110, align: 'num', priority: 3 },
]);

type TypeFilter = 'all' | AudienceType;
type SizeBand = 'all' | 'small' | 'medium' | 'large';
type Usage = 'all' | 'unused' | 'lt3' | 'gte3' | 'archived';
type Sort = 'size_desc' | 'size_asc' | 'name' | 'recent';

export interface AudiencesViewProps {
  /** An audience to highlight, with a dismissible banner above the list. */
  flashId?: string;
  clearFlash?: () => void;
}

function AudiencesView({ flashId, clearFlash }: AudiencesViewProps) {
  const navigate = useNavigate();
  const audienceQuery = useAudiences();
  const campaignQuery = useCampaigns();
  const archiveAudience = useArchiveAudience();
  const restoreAudience = useRestoreAudience();
  const refreshAudience = useRefreshAudience();
  const refreshAudienceSize = useRefreshAudienceSize();
  const audiences = audienceQuery.data ?? [];
  const campaigns = campaignQuery.data ?? [];
  const [archivedRows, setArchivedRows] = useState<ListedAudience[]>([]);

  const prodCountByAudience = useMemo(() => {
    const accumulator = new Map<string, Set<string>>();
    for (const campaign of campaigns) {
      if (!campaign.audience_id || !campaign.production_id) continue;
      const set = accumulator.get(campaign.audience_id) || new Set();
      set.add(campaign.production_id);
      accumulator.set(campaign.audience_id, set);
    }
    return Object.fromEntries(
      [...accumulator.entries()].map(([audienceId, productions]) => [
        audienceId,
        productions.size,
      ]),
    );
  }, [campaigns]);

  const archivedIds = useMemo(
    () => new Set(archivedRows.map((audience) => audience.id)),
    [archivedRows],
  );
  const activeAudiences = useMemo(
    () => audiences.filter((audience) => !archivedIds.has(audience.id)),
    [archivedIds, audiences],
  );
  const flashed = activeAudiences.find((audience) => audience.id === flashId);
  const [q, setQ] = useState('');
  const [type, setType] = useState<TypeFilter>('all');
  const [sizeBand, setSizeBand] = useState<SizeBand>('all');
  const [usage, setUsage] = useState<Usage>('all');
  const [sort, setSort] = useState<Sort>('size_desc');
  const [selected, setSelected] = useState<ListedAudience | null>(null);

  const list = useMemo(() => {
    let output: ListedAudience[] = usage === 'archived' ? archivedRows : activeAudiences;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      output = output.filter((audience) =>
        audience.name.toLowerCase().includes(needle)
        || audience.id.toLowerCase().includes(needle));
    }
    if (type !== 'all') {
      output = output.filter((audience) => audience.audience_type === type);
    }
    if (sizeBand !== 'all') {
      output = output.filter((audience) => {
        const size = audience.estimated_size ?? 0;
        if (sizeBand === 'small') return size < 50_000;
        if (sizeBand === 'medium') return size >= 50_000 && size < 250_000;
        return size >= 250_000;
      });
    }
    if (usage !== 'all' && usage !== 'archived') {
      output = output.filter((audience) => {
        const productionCount = prodCountByAudience[audience.id] || 0;
        if (usage === 'unused') return productionCount === 0;
        if (usage === 'lt3') return productionCount > 0 && productionCount < 3;
        return productionCount >= 3;
      });
    }
    output = [...output];
    if (sort === 'size_desc') {
      output.sort((left, right) => (right.estimated_size ?? 0) - (left.estimated_size ?? 0));
    } else if (sort === 'size_asc') {
      output.sort((left, right) => (left.estimated_size ?? 0) - (right.estimated_size ?? 0));
    } else if (sort === 'name') {
      output.sort((left, right) => left.name.localeCompare(right.name));
    } else if (sort === 'recent') {
      output.sort((left, right) => right.updated_at.localeCompare(left.updated_at));
    }
    return output;
  }, [activeAudiences, archivedRows, prodCountByAudience, q, sizeBand, sort, type, usage]);


  const actionError = archiveAudience.error
    || restoreAudience.error
    || refreshAudience.error
    || refreshAudienceSize.error;
  const actionPending = archiveAudience.isPending
    || restoreAudience.isPending
    || refreshAudience.isPending
    || refreshAudienceSize.isPending;

  const archiveOne = async (audience: ListedAudience) => {
    try {
      await archiveAudience.mutateAsync(audience.id);
      setArchivedRows((current) => [
        { ...audience, archived: true },
        ...current.filter((entry) => entry.id !== audience.id),
      ]);
      setSelected(null);
    } catch {
      // The shared mutation error remains visible in the open drawer.
    }
  };
  const restoreOne = async (audience: ListedAudience) => {
    try {
      const restored = await restoreAudience.mutateAsync(audience.id);
      setArchivedRows((current) => current.filter((entry) => entry.id !== audience.id));
      setSelected(restored);
    } catch {
      // The shared mutation error remains visible in the open drawer.
    }
  };
  const refreshOne = async (audience: ListedAudience) => {
    // Two independent asks. A 409 from /refresh (provider work already in
    // flight) must not skip the size recompute — each mutation's error stays
    // visible through its own hook.
    let refreshed: ListedAudience | null = null;
    try {
      refreshed = await refreshAudience.mutateAsync(audience.id);
    } catch {
      // Surfaced via refreshAudience.error in the drawer.
    }
    try {
      refreshed = await refreshAudienceSize.mutateAsync(audience.id);
    } catch {
      // Surfaced via refreshAudienceSize.error in the drawer.
    }
    if (refreshed) setSelected(refreshed);
    return refreshed;
  };

  const exportRows = () => {
    downloadCsv(csvFilename('audiences'), list.map((audience) => ({
      'Audience ID': audience.id,
      Name: audience.name,
      Description: audience.description ?? '',
      Type: audience.audience_type,
      Status: audience.archived ? 'archived' : 'active',
      'Estimated size': audience.estimated_size ?? '',
      'Size estimated at': audience.size_estimated_at ?? '',
      'Used in productions': prodCountByAudience[audience.id] || 0,
      'Sync status': audience.sync?.status ?? '',
      'Sync target': audience.sync?.target ?? '',
      'Rule leaves': countLeaves(audience.rules),
      Rules: JSON.stringify(audience.rules),
      'Created at': audience.created_at,
      'Updated at': audience.updated_at,
    })));
  };

  return (
    <div className="tbl-frame">
      <div className="page-hd">
        <div><h1 className="page-title">Audiences</h1></div>
        <PageActions>
          <button className="btn" onClick={exportRows} disabled={list.length === 0}><span>{Icons.dl}</span>Export</button>
          <Link className="btn btn-primary" data-phone="inline" to="/audiences/new" preload="intent"><span>{Icons.plus}</span><span className="phone-long">New Audience</span><span className="phone-short">New</span></Link>
        </PageActions>
      </div>

      {flashed && (
        <div role="status" className="audience-created-flash" style={{margin:'12px 22px 0',padding:'10px 14px',display:'flex',alignItems:'center',gap:10,background:'color-mix(in oklch, var(--good) 12%, var(--bg-elev))',border:'1px solid color-mix(in oklch, var(--good) 40%, var(--line))',borderRadius:'var(--r)'}}>
          <span style={{color:'var(--good)',fontSize:14}}>{Icons.check}</span>
          <span style={{fontSize:12.5}}>Audience <b>{flashed.name}</b> created · {visualTypeFromAudienceType(flashed.audience_type)} · {flashed.estimated_size != null ? `${flashed.estimated_size.toLocaleString()} people` : 'size pending'}</span>
          <button className="btn btn-ghost btn-sm" style={{marginLeft:'auto',padding:'2px 8px'}} onClick={clearFlash} aria-label="Dismiss">{Icons.x}</button>
        </div>
      )}

      <ResponsiveFilters className="filter-bar">
        <div className="chip-input" style={{flex:'1 1 0',minWidth:240}}>
          <span style={{color:'var(--ink-3)'}}>{Icons.search}</span>
          <input value={q} onChange={(event) => setQ(event.target.value)} placeholder="Filter audiences by name or ID…"/>
        </div>
        <Dropdown label="TYPE" value={type} options={[
          { id: 'all', label: 'All types' },
          { id: 'direct', label: 'Direct', dot: 'var(--ink-1)' },
          { id: 'lookalike', label: 'Lookalike', dot: 'var(--chart-3)' },
          { id: 'retargeting', label: 'Retargeting', dot: 'var(--chart-2)' },
        ]} onChange={setType} width={150}/>
        <Dropdown label="SIZE" value={sizeBand} options={[
          { id: 'all', label: 'Any size' },
          { id: 'small', label: '< 50K' },
          { id: 'medium', label: '50K – 250K' },
          { id: 'large', label: '> 250K' },
        ]} onChange={setSizeBand} width={150}/>
        <Dropdown label="USAGE" value={usage} options={[
          { id: 'all', label: 'Any usage' },
          { id: 'unused', label: 'Unused (0 productions)' },
          { id: 'lt3', label: '1 – 2 productions' },
          { id: 'gte3', label: '3+ productions' },
          { id: 'archived', label: `Archived this session (${archivedRows.length})` },
        ]} onChange={setUsage} width={230}/>
        <Dropdown label="SORT" value={sort} options={[
          { id: 'size_desc', label: 'Size · largest first' },
          { id: 'size_asc', label: 'Size · smallest first' },
          { id: 'name', label: 'Name · A → Z' },
          { id: 'recent', label: 'Most recently refreshed' },
        ]} onChange={setSort} width={210} rightAlign/>
      </ResponsiveFilters>

      {actionError && (
        <div style={{padding:'8px 22px 0'}}><MutationErrorLine error={actionError}/></div>
      )}

      {/* The archived view is a browser-session list, not a server read, so it
          supplies its own totals and declares no query. That is what turns the
          five hand-written arms into the standard four: with no dependency the
          table cannot report loading or error for a collection the server never
          served, and `empty` says which collection is empty. */}
      <Table
        rows={list}
        rowH={ROW_H}
        unfilteredTotal={usage === 'archived' ? archivedRows.length : activeAudiences.length}
        deps={usage === 'archived' ? [] : [{ what: 'audiences', query: audienceQuery }]}
        empty={usage === 'archived'
          ? 'No audiences archived in this browser session.'
          : 'No audiences yet. Create one to get started.'}
        noMatch="No audiences match these filters."
        onRowClick={setSelected}
        rowStyle={(audience) => (audience.id === flashId
          ? { background: 'color-mix(in oklch, var(--good) 8%, transparent)' }
          : undefined)}
        renderRow={(audience) => (
          <>
            <td className="name">
              {audience.name}{audience.archived ? ' · archived' : ''}
              {/* Identifiable without opening it: this row is maintained
                  by the cron from Admin → Config, and every edit control
                  inside the drawer is refused. */}
              {isManagedAudience(audience) && (
                <span className="muted mono" style={{fontSize:9.5,letterSpacing:'.1em',marginLeft:6}}>
                  · MANAGED BY SAM
                </span>
              )}
            </td>
            <td><span className="tag">{audience.audience_type}</span></td>
            <td className="num">{audience.estimated_size?.toLocaleString() ?? '—'}</td>
            <td className="num">{prodCountByAudience[audience.id] || 0}</td>
            <td className="muted">{ago(audience.updated_at)}</td>
            <td><span className="tag">{audienceSyncStateWords(audience.sync?.status, audience.sync?.target) ?? 'Not started'}</span></td>
            <td className="muted">{audience.size_estimated_at ? ago(audience.size_estimated_at) : '—'}</td>
            <td className="num">{countLeaves(audience.rules)}</td>
          </>
        )}
      />

      {selected && (
        <AudienceDetailDrawer
          audience={
            activeAudiences.find((audience) => audience.id === selected.id)
            || archivedRows.find((audience) => audience.id === selected.id)
            || selected
          }
          productionCount={prodCountByAudience[selected.id] || 0}
          campaigns={campaigns}
          actionError={actionError}
          actionPending={actionPending}
          onClose={() => setSelected(null)}
          onDuplicate={(audience) => {
            try {
              sessionStorage.setItem(
                'sam:audience-duplicate',
                JSON.stringify(audience),
              );
            } catch {
              // The active-list query still supplies the normal duplicate path.
            }
            setSelected(null);
            void navigate({
              to: '/audiences/new',
              search: { duplicate: audience.id },
            });
          }}
          onArchive={archiveOne}
          onRestore={restoreOne}
          onRefresh={refreshOne}
        />
      )}
    </div>
  );
}

export { AudiencesView };
